import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Terminal, Lock, Mail, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        email,
        password
      });

      if (response.data.success) {
        // Store auth in sessionStorage
        sessionStorage.setItem('admin_auth', 'true');
        toast.success('Access granted');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Terminal className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl font-bold font-mono text-cyan-400 tracking-wider">NARRATIVE SCOUT</h1>
          </div>
          <p className="text-sm text-gray-500 font-mono">// Admin Access Terminal</p>
        </div>

        {/* Login Card */}
        <Card className="border-cyan-500/30 bg-black/60 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl font-mono text-cyan-400 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              AUTHENTICATION REQUIRED
            </CardTitle>
            <CardDescription className="text-gray-500 font-mono text-xs">
              // Enter credentials to access admin panel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-2">EMAIL</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/40 border border-cyan-500/30 rounded px-10 py-2 text-gray-100 font-mono text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
                    placeholder="admin@example.com"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-2">PASSWORD</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/40 border border-cyan-500/30 rounded px-10 py-2 text-gray-100 font-mono text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
                    placeholder="••••••••••••••••"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50 font-mono transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    AUTHENTICATING...
                  </>
                ) : (
                  'ACCESS ADMIN PANEL'
                )}
              </Button>

              {/* Back to Home */}
              <Button
                type="button"
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-gray-600 text-gray-400 hover:bg-gray-800/50 font-mono"
                disabled={loading}
              >
                RETURN TO HOME
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-600 font-mono">
            // Unauthorized access is prohibited
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

